import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function ActivarCuentaPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Token de activación no válido');
    }
  }, [token]);

  const validatePassword = (pass: string) => {
    if (pass.length < 8) {
      return 'La contraseña debe tener al menos 8 caracteres';
    }
    if (!/[A-Z]/.test(pass)) {
      return 'La contraseña debe contener al menos una mayúscula';
    }
    if (!/[a-z]/.test(pass)) {
      return 'La contraseña debe contener al menos una minúscula';
    }
    if (!/[0-9]/.test(pass)) {
      return 'La contraseña debe contener al menos un número';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validaciones
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/activar-cuenta`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            password,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al activar la cuenta');
      }

      setEmail(data.email);
      setSuccess(true);

      // Redirigir al login después de 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Error al activar la cuenta');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-line text-4xl text-teal-600"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            ¡Cuenta activada!
          </h1>
          <p className="text-gray-600 mb-6">
            Tu cuenta ha sido activada exitosamente. Ya puedes iniciar sesión con tu email:
          </p>
          <div className="bg-teal-50 rounded-lg p-4 mb-6">
            <p className="font-semibold text-teal-900">{email}</p>
          </div>
          <p className="text-sm text-gray-500">
            Serás redirigido al login en unos segundos...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-lock-password-line text-3xl text-teal-600"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Activa tu cuenta
          </h1>
          <p className="text-gray-600">
            Crea tu contraseña para comenzar a usar la aplicación
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <i className="ri-error-warning-line text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nueva contraseña
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Mínimo 8 caracteres"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                <i className={`ri-eye-${showPassword ? 'off' : ''}-line text-xl`}></i>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar contraseña
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Repite tu contraseña"
              required
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              La contraseña debe contener:
            </p>
            <ul className="space-y-1 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <i className={`ri-checkbox-circle-${password.length >= 8 ? 'fill text-teal-600' : 'line text-gray-400'} text-lg`}></i>
                Al menos 8 caracteres
              </li>
              <li className="flex items-center gap-2">
                <i className={`ri-checkbox-circle-${/[A-Z]/.test(password) ? 'fill text-teal-600' : 'line text-gray-400'} text-lg`}></i>
                Una letra mayúscula
              </li>
              <li className="flex items-center gap-2">
                <i className={`ri-checkbox-circle-${/[a-z]/.test(password) ? 'fill text-teal-600' : 'line text-gray-400'} text-lg`}></i>
                Una letra minúscula
              </li>
              <li className="flex items-center gap-2">
                <i className={`ri-checkbox-circle-${/[0-9]/.test(password) ? 'fill text-teal-600' : 'line text-gray-400'} text-lg`}></i>
                Un número
              </li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={loading || !token}
            className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Activando cuenta...
              </span>
            ) : (
              'Activar cuenta'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            ¿Problemas con el link?{' '}
            <a href="/login" className="text-teal-600 hover:text-teal-700 font-medium">
              Contacta al gimnasio
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}